import { Link } from 'react-router-dom';
import { Logo } from './Logo';

/**
 * #227 — UK trading-disclosure rules require the registered name, place of
 * registration, company number and registered office on the website, and the
 * E-Commerce Regulations want them "easily, directly and permanently accessible",
 * so they sit on every page rather than only in the Terms. Matches Companies
 * House as of 2026-10-06. Pages that render without this footer (the 404) use
 * this component so the wording cannot drift; the email footer must say the
 * same thing word for word (test_notification_legal_footer.py reads this file).
 */
export function CompanyDisclosure({ className }: { className?: string }) {
  return (
    <p className={className}>
      SOCIALYSE.AI LTD · Registered in England and Wales, company number 17106656 ·
      Registered office: 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ
    </p>
  );
}

/** White footer with brand mark, blurb, and legal links — V3 site template. */
export function SiteFooter() {
  return (
    <footer className="bg-white">
      <div className="mx-auto max-w-6xl border-t border-[#e6ebf1] px-6 py-8">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#001830]">
            <Logo size={22} className="text-[#1d2fb5]" />
            <span className="text-lg font-bold tracking-tight">socialyse.ai</span>
          </Link>

          <div className="max-w-md">
            <p className="text-sm leading-relaxed text-[#001830]/60">
              {/* #213 — the brand is always lowercase in user-facing copy, even
                  sentence-initial. SOCIALYSE.AI LTD, the registered company
                  name, is a separate and correct usage. This footer renders on
                  every authenticated page, so it was the most-seen instance. */}
              socialyse.ai provides AI-powered social media screening for U.S.
              visa applicants. Analyse your digital footprint, identify potential
              risks, and receive actionable guidance before your application.
            </p>
            {/* Ad platforms (Google's government-documents policy, Meta, LinkedIn) reject
                anything that implies a government link; the ads' end cards carry the same line. */}
            <p className="mt-2 text-sm leading-relaxed text-[#001830]/60">
              socialyse.ai is an independent service and is not affiliated with the US government.
            </p>
            <div className="mt-4 flex flex-wrap gap-6 text-sm font-semibold">
              <Link to="/pricing" className="text-[#001830] hover:underline">Pricing</Link>
              <Link to="/privacy" className="text-[#001830] hover:underline">Privacy Policy</Link>
              <Link to="/terms" className="text-[#001830] hover:underline">Terms of Use</Link>
              {/* #162 §5.2/§6 row 20, #158 §6 item 4 — reg 13(1)(b) requires the
                  statutory cancellation form be made available before the
                  consumer is bound; the footer is where every other legal
                  link already lives. */}
              <Link to="/legal/cancellation-form" className="text-[#001830] hover:underline">Cancellation Form</Link>
              {/* #227 — a page, not a mailto: a mailto does nothing visible for anyone
                  using webmail. The address is also shown as text so it can be copied. */}
              <Link to="/contact" className="text-[#001830] hover:underline">Contact</Link>
              <a href="mailto:support@socialyse.ai" className="text-[#001830] hover:underline">support@socialyse.ai</a>
            </div>
          </div>
        </div>
        <CompanyDisclosure className="mt-8 border-t border-[#e6ebf1] pt-6 text-xs leading-relaxed text-[#001830]/60" />
      </div>
    </footer>
  );
}
